import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ImpersonationPanel } from "@/components/ImpersonationPanel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import {
  Users,
  Activity,
  Clock,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Zap,
  UserCog,
  Shield,
  UserX,
  UserCheck,
  UserPlus,
  Mail,
  Trash2,
  Calendar,
  Play,
  Pause,
  FileText,
  ClipboardList,
  CheckSquare,
  Send,
  History,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Circle,
  Search,
  Download,
  Timer,
  BarChart3,
  Plus,
  Sparkles,
} from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from "date-fns";
import { RichTextEditor } from "@/components/RichTextEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [therapists, setTherapists] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]);
  const [availabilityBlocks, setAvailabilityBlocks] = useState([]);
  const [facilitatorActions, setFacilitatorActions] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  
  // Therapist status with colors (green/orange/blue)
  const [therapistStatuses, setTherapistStatuses] = useState([]);
  
  // Therapist calendar view
  const [selectedTherapistForCalendar, setSelectedTherapistForCalendar] = useState(null);
  const [therapistCalendarData, setTherapistCalendarData] = useState(null);
  const [therapistCalendarMonth, setTherapistCalendarMonth] = useState(new Date());
  
  // Admin calendar view (all therapists)
  const [adminCalendarMonth, setAdminCalendarMonth] = useState(new Date());
  const [adminCalendarData, setAdminCalendarData] = useState({ availability_blocks: [], appointments: [], sessions: [] });
  const [selectedAdminCalendarDate, setSelectedAdminCalendarDate] = useState(new Date());
  // Req #2: Day view toggle for master calendar (stacked list of all therapists)
  const [adminCalendarMode, setAdminCalendarMode] = useState("month"); // "month" | "day"
  const [adminDayViewDate, setAdminDayViewDate] = useState(new Date());
  
  // Pre-registration state
  const [preRegisterDialogOpen, setPreRegisterDialogOpen] = useState(false);
  const [preRegisterEmail, setPreRegisterEmail] = useState("");
  const [preRegisterRoles, setPreRegisterRoles] = useState(["therapist"]);
  const [preRegisterName, setPreRegisterName] = useState("");
  const [preRegistering, setPreRegistering] = useState(false);
  
  // Search state for all tabs
  const [appointmentSearch, setAppointmentSearch] = useState("");
  const [sessionSearch, setSessionSearch] = useState("");
  const [actionSearch, setActionSearch] = useState("");
  const [emailSearch, setEmailSearch] = useState("");
  const [waitlistSearch, setWaitlistSearch] = useState("");
  const [auditSearch, setAuditSearch] = useState("");
  
  // KPI Period selector (7, 30, 90 days)
  const [kpiPeriod, setKpiPeriod] = useState(30);
  
  // Session Audit Trails state
  const [sessionAuditTrails, setSessionAuditTrails] = useState([]);
  const [selectedAuditTrail, setSelectedAuditTrail] = useState(null);
  const [auditDetailOpen, setAuditDetailOpen] = useState(false);
  const [exportingAudit, setExportingAudit] = useState(false);
  
  // Collapsible section states
  const [therapistStatusExpanded, setTherapistStatusExpanded] = useState(false);

  // What's New posts state
  const [whatsNewPosts, setWhatsNewPosts] = useState([]);
  const [whatsNewEditing, setWhatsNewEditing] = useState(null); // { id?, title, body_html, active }
  const [whatsNewSaving, setWhatsNewSaving] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 30 seconds
    const refreshInterval = setInterval(() => {
      fetchData();
    }, 30000);
    
    return () => clearInterval(refreshInterval);
  }, []);
  
  // Refetch KPIs when period changes
  useEffect(() => {
    fetchKpis();
  }, [kpiPeriod]);
  
  const fetchKpis = async () => {
    try {
      const kpisRes = await axios.get(`${API}/stats/kpi?period=${kpiPeriod}`).catch(() => ({ data: null }));
      setKpis(kpisRes.data);
    } catch (error) {
      console.error("Error fetching KPIs:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, kpisRes, therapistsRes, sessionsRes, usersRes, appointmentsRes, blocksRes, actionsRes, emailsRes, therapistStatusRes, waitlistRes, whatsNewRes, auditRes] = await Promise.all([
        axios.get(`${API}/stats/overview`).catch(() => ({ data: {} })),
        axios.get(`${API}/stats/kpi?period=${kpiPeriod}`).catch(() => ({ data: null })),
        axios.get(`${API}/therapists`).catch(() => ({ data: { therapists: [] } })),
        axios.get(`${API}/sessions`).catch(() => ({ data: { sessions: [] } })),
        axios.get(`${API}/admin/users`).catch(() => ({ data: { users: [] } })),
        axios.get(`${API}/appointments`).catch(() => ({ data: { appointments: [] } })),
        axios.get(`${API}/availability-blocks`).catch(() => ({ data: { blocks: [] } })),
        axios.get(`${API}/admin/facilitator-actions`).catch(() => ({ data: { actions: [] } })),
        axios.get(`${API}/admin/email-logs`).catch(() => ({ data: { logs: [] } })),
        axios.get(`${API}/admin/therapists/availability-status`).catch(() => ({ data: { therapists: [] } })),
        axios.get(`${API}/waitlist`).catch(() => ({ data: { waitlist: [] } })),
        axios.get(`${API}/admin/whats-new`).catch(() => ({ data: { posts: [] } })),
        axios.get(`${API}/admin/session-audit-trails?limit=100`).catch(() => ({ data: { audit_trails: [] } })),
      ]);
      setStats(statsRes.data || {});
      setKpis(kpisRes.data);
      setTherapists(therapistsRes.data.therapists || []);
      setSessions(sessionsRes.data.sessions || []);
      setAllUsers(usersRes.data.users || []);
      setAllAppointments(appointmentsRes.data.appointments || []);
      setAvailabilityBlocks(blocksRes.data.blocks || []);
      setFacilitatorActions(actionsRes.data.actions || []);
      setEmailLogs(emailsRes.data.logs || []);
      setTherapistStatuses(therapistStatusRes.data.therapists || []);
      setWaitlist(waitlistRes.data.waitlist || []);
      setSessionAuditTrails(auditRes.data.audit_trails || []);
      setWhatsNewPosts(whatsNewRes.data.posts || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      // Only show error if we couldn't load anything at all
      if (!stats && !therapists.length) {
        toast.error("Failed to load dashboard data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch therapist calendar when selected
  const fetchTherapistCalendar = async (therapistId) => {
    if (!therapistId) {
      setTherapistCalendarData(null);
      return;
    }
    try {
      const month = therapistCalendarMonth.getMonth() + 1;
      const year = therapistCalendarMonth.getFullYear();
      const response = await axios.get(`${API}/therapists/${therapistId}/calendar?month=${month}&year=${year}`);
      setTherapistCalendarData(response.data);
    } catch (error) {
      console.error("Failed to fetch therapist calendar:", error);
      toast.error("Failed to load therapist calendar");
    }
  };

  useEffect(() => {
    if (selectedTherapistForCalendar) {
      fetchTherapistCalendar(selectedTherapistForCalendar);
    }
  }, [selectedTherapistForCalendar, therapistCalendarMonth]);

  // Fetch admin calendar data
  const fetchAdminCalendar = useCallback(async () => {
    try {
      const month = adminCalendarMonth.getMonth() + 1;
      const year = adminCalendarMonth.getFullYear();
      const response = await axios.get(`${API}/admin/calendar?month=${month}&year=${year}`);
      setAdminCalendarData(response.data);
    } catch (error) {
      console.error("Failed to fetch admin calendar:", error);
    }
  }, [adminCalendarMonth]);

  useEffect(() => {
    fetchAdminCalendar();
  }, [adminCalendarMonth, fetchAdminCalendar]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Dashboard refreshed");
  };

  const [phoneDrafts, setPhoneDrafts] = useState({});
  const [savingPhoneId, setSavingPhoneId] = useState(null);
  const [syncingNames, setSyncingNames] = useState(false);
  const [editingTherapist, setEditingTherapist] = useState(null);
  const [savingTherapist, setSavingTherapist] = useState(false);

  const handleSaveTherapistProfile = async () => {
    if (!editingTherapist) return;
    setSavingTherapist(true);
    try {
      await axios.put(`${API}/admin/therapists/${editingTherapist.id}`, {
        name: editingTherapist.name,
        phone: editingTherapist.phone ?? "",
        specialty: editingTherapist.specialty,
        max_concurrent: Number(editingTherapist.max_concurrent) || 1,
      });
      toast.success("Therapist profile updated");
      setEditingTherapist(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update therapist");
    } finally {
      setSavingTherapist(false);
    }
  };

  const handleSaveTherapistPhone = async (therapistId) => {
    setSavingPhoneId(therapistId);
    try {
      await axios.put(`${API}/admin/therapists/${therapistId}/phone`, {
        phone: phoneDrafts[therapistId] ?? "",
      });
      toast.success("Phone number saved");
      setPhoneDrafts((prev) => {
        const next = { ...prev };
        delete next[therapistId];
        return next;
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save phone number");
    } finally {
      setSavingPhoneId(null);
    }
  };

  const handleSyncTherapistNames = async () => {
    setSyncingNames(true);
    try {
      const res = await axios.post(`${API}/admin/sync-therapist-names`);
      const count = res.data.updated_count || 0;
      toast.success(count > 0 ? `${count} therapist name(s) updated` : "All therapist names already up to date");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync names");
    } finally {
      setSyncingNames(false);
    }
  };


  const handleRolesChange = async (userId, newRoles) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/roles`, { roles: newRoles });
      toast.success(`User roles updated to ${newRoles.join(", ")}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update roles");
    }
  };

  // Helper to get roles array from user (handles legacy single role)
  const getUserRoles = (u) => {
    if (u.roles && Array.isArray(u.roles)) return u.roles;
    if (u.role === "admin_therapist") return ["admin", "therapist"];
    if (u.role) return [u.role];
    return ["facilitator"];
  };

  // Toggle a specific role for a user
  const toggleUserRole = (userId, role, currentRoles) => {
    const newRoles = currentRoles.includes(role)
      ? currentRoles.filter(r => r !== role)
      : [...currentRoles, role];
    
    // Ensure at least one role remains
    if (newRoles.length === 0) {
      toast.error("User must have at least one role");
      return;
    }
    
    handleRolesChange(userId, newRoles);
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      if (isActive) {
        await axios.put(`${API}/admin/users/${userId}/deactivate`);
        toast.success("User deactivated");
      } else {
        await axios.put(`${API}/admin/users/${userId}/activate`);
        toast.success("User activated");
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handlePreRegister = async () => {
    if (!preRegisterEmail) {
      toast.error("Email is required");
      return;
    }
    
    if (!preRegisterEmail.endsWith("@therapymgmt.com")) {
      toast.error("Only @therapymgmt.com email addresses can be pre-registered");
      return;
    }

    if (preRegisterRoles.length === 0) {
      toast.error("At least one role is required");
      return;
    }
    
    setPreRegistering(true);
    try {
      const response = await axios.post(`${API}/admin/users/pre-register`, {
        email: preRegisterEmail,
        roles: preRegisterRoles,
        name: preRegisterName || null
      });
      toast.success(response.data.message);
      setPreRegisterDialogOpen(false);
      setPreRegisterEmail("");
      setPreRegisterRoles(["therapist"]);
      setPreRegisterName("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to pre-register user");
    } finally {
      setPreRegistering(false);
    }
  };

  const handleDeletePreRegistered = async (userId, email) => {
    try {
      await axios.delete(`${API}/admin/users/pre-registered/${userId}`);
      toast.success(`Pre-registered user ${email} deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete pre-registered user");
    }
  };

  const handleCancelWaitlistEntry = async (entryId, patientName) => {
    if (!window.confirm(`Remove "${patientName}" from the waitlist?`)) return;
    try {
      await axios.delete(`${API}/waitlist/${entryId}`);
      toast.success(`Waitlist entry for ${patientName} removed`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove waitlist entry");
    }
  };

  // What's New handlers
  const openWhatsNewNew = () => setWhatsNewEditing({ title: "", body_html: "", active: true, audience: [] });
  const openWhatsNewEdit = (post) =>
    setWhatsNewEditing({
      id: post.id,
      title: post.title,
      body_html: post.body_html,
      active: post.active,
      audience: post.audience || [],
      reset_dismissals: false,
    });
  const closeWhatsNewEditor = () => setWhatsNewEditing(null);

  const saveWhatsNew = async () => {
    if (!whatsNewEditing) return;
    const { id, title, body_html, active, audience, reset_dismissals } = whatsNewEditing;
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (!body_html.trim() || body_html === "<br>") { toast.error("Body cannot be empty"); return; }
    setWhatsNewSaving(true);
    try {
      const body = { title, body_html, active, audience: audience && audience.length ? audience : null };
      if (id) {
        await axios.put(`${API}/admin/whats-new/${id}`, { ...body, reset_dismissals: !!reset_dismissals });
        toast.success(reset_dismissals ? "Updated and re-shown to all users" : "Post updated");
      } else {
        await axios.post(`${API}/admin/whats-new`, body);
        toast.success("What's New post created — users will see it on next page load");
      }
      setWhatsNewEditing(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setWhatsNewSaving(false);
    }
  };

  const toggleAudienceRole = (role) => {
    if (!whatsNewEditing) return;
    const current = whatsNewEditing.audience || [];
    const next = current.includes(role) ? current.filter((r) => r !== role) : [...current, role];
    setWhatsNewEditing({ ...whatsNewEditing, audience: next });
  };

  const deleteWhatsNew = async (postId) => {
    if (!window.confirm("Delete this post? Users who have already dismissed it won't be affected, but it will be removed from the list.")) return;
    try {
      await axios.delete(`${API}/admin/whats-new/${postId}`);
      toast.success("Post deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const toggleWhatsNewActive = async (post) => {
    try {
      await axios.put(`${API}/admin/whats-new/${post.id}`, { active: !post.active });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    }
  };

  // Export session audit trails as CSV
  const handleExportAuditTrails = async () => {
    setExportingAudit(true);
    try {
      const response = await axios.get(`${API}/admin/session-audit-trails/export/csv`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `session_audit_trails_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Audit trails exported successfully");
    } catch (error) {
      toast.error("Failed to export audit trails");
    } finally {
      setExportingAudit(false);
    }
  };

  // Format seconds to human readable
  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return "—";
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  };

  const therapistStatusData = therapists.reduce(
    (acc, t) => {
      acc[t.status] = (acc[t.status] || 0) + 1;
      return acc;
    },
    { available: 0, busy: 0, offline: 0 }
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="admin-dashboard" className="space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
              Operations Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              System overview and KPI monitoring
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {/* Period Selector */}
            <Select value={kpiPeriod.toString()} onValueChange={(val) => setKpiPeriod(parseInt(val))}>
              <SelectTrigger className="w-[140px]" data-testid="kpi-period-select">
                <SelectValue placeholder="Time Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 Days</SelectItem>
                <SelectItem value="30">Last 30 Days</SelectItem>
                <SelectItem value="90">Last 90 Days</SelectItem>
                <SelectItem value="365">Last 365 Days</SelectItem>
                <SelectItem value="0">All Time</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Real-Time Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="card-hover border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Active Sessions</p>
                  <p className="text-2xl font-bold mt-1">{stats?.active_sessions || 0}</p>
                  <p className="text-xs text-muted-foreground">
                    Capacity: {kpis?.current_sessions || 0}/{kpis?.total_capacity || 0}
                  </p>
                </div>
                <Activity className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Queue Length</p>
                  <p className="text-2xl font-bold mt-1">{stats?.queue_length || 0}</p>
                  <p className="text-xs text-muted-foreground">Patients waiting</p>
                </div>
                <Users className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Today</p>
                  <p className="text-2xl font-bold mt-1">{stats?.completed_today || 0}</p>
                  <p className="text-xs text-muted-foreground">Sessions completed</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Period KPI Cards */}
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-muted-foreground">
            Performance Metrics ({kpiPeriod === 0 ? "All Time" : `${kpiPeriod} Day Period`})
          </h2>
          {/* No data warning - show when period has 0 but DB has data */}
          {kpis && kpis.total_appointments === 0 && kpis.debug_total_in_db?.appointments > 0 && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800" data-testid="kpi-no-data-warning">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>
                No appointments found in the selected period. The database has {kpis.debug_total_in_db.appointments} total appointments.
                Try selecting <strong>All Time</strong> or a longer period to see historical data.
              </span>
            </div>
          )}
          {kpis && kpis.total_appointments > 0 && (
            <p className="text-xs text-muted-foreground">
              Showing {kpis.debug_in_period?.appointments || kpis.total_appointments} appointments
              {kpis.debug_total_in_db?.appointments > 0 && ` (${kpis.debug_total_in_db.appointments} total in database)`}
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="card-hover">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Appointments</p>
                    <p className="text-3xl font-bold mt-1">{kpis?.total_appointments || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {kpis?.avg_daily_appointments || 0} avg/day
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Sessions Completed</p>
                    <p className="text-3xl font-bold mt-1">{kpis?.completed_sessions || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      of {kpis?.total_sessions || 0} total sessions
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Completion Rate</p>
                    <p className="text-3xl font-bold mt-1">
                      {kpis?.completion_rate || 0}%
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {kpis?.completed_appointments || 0} completed
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <Progress value={kpis?.completion_rate || 0} className="mt-4 h-2" />
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Canceled/Missed</p>
                    <p className="text-3xl font-bold mt-1 text-red-600">
                      {(kpis?.canceled_appointments || 0) + (kpis?.missed_appointments || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {kpis?.canceled_appointments || 0} canceled, {kpis?.missed_appointments || 0} no-show
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Breakdown Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* By Discipline */}
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground mb-3">By Discipline ({kpiPeriod === 0 ? "All" : `${kpiPeriod}d`})</p>
              {kpis?.by_discipline && Object.keys(kpis.by_discipline).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(kpis.by_discipline).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([disc, count]) => (
                    <div key={disc} className="flex items-center justify-between text-sm">
                      <span className="truncate">{disc}</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No data available</p>
              )}
            </CardContent>
          </Card>

          {/* By State */}
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground mb-3">By State ({kpiPeriod === 0 ? "All" : `${kpiPeriod}d`})</p>
              {kpis?.by_state && Object.keys(kpis.by_state).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(kpis.by_state).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([state, count]) => (
                    <div key={state} className="flex items-center justify-between text-sm">
                      <span className="truncate">{state}</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No data available</p>
              )}
            </CardContent>
          </Card>

          {/* Top Performers */}
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground mb-3">Top Therapists ({kpiPeriod === 0 ? "All" : `${kpiPeriod}d`})</p>
              {kpis?.top_therapists?.length > 0 ? (
                <div className="space-y-2">
                  {kpis.top_therapists.slice(0, 5).map((t, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="truncate">{t.name}</span>
                      <Badge variant="secondary">{t.sessions} sessions</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No data available</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Admin Calendar - All Therapists Availability & Appointments */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Master Availability Calendar
                </CardTitle>
                <CardDescription>
                  All therapists&apos; availability (blue) and scheduled appointments (red).
                  <span className="block mt-0.5 text-emerald-700 font-medium">
                    👉 Click any day to see how much free appointment time each therapist has left.
                  </span>
                </CardDescription>
              </div>
              <div className="flex items-center gap-4 text-xs flex-wrap">
                {/* Req #2: Month / Day view toggle */}
                <div className="inline-flex rounded-md border bg-background p-0.5" data-testid="admin-calendar-mode-toggle">
                  <button
                    onClick={() => setAdminCalendarMode("month")}
                    className={`px-3 py-1 text-xs rounded ${adminCalendarMode === "month" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="admin-calendar-mode-month"
                  >
                    Month
                  </button>
                  <button
                    onClick={() => {
                      setAdminCalendarMode("day");
                      // Sync the loaded month to the day's month so data is available.
                      const d = adminDayViewDate || new Date();
                      setAdminCalendarMonth(new Date(d.getFullYear(), d.getMonth(), 1));
                    }}
                    className={`px-3 py-1 text-xs rounded ${adminCalendarMode === "day" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="admin-calendar-mode-day"
                  >
                    Day
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-blue-500"></div>
                  <span className="text-muted-foreground">Availability</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-red-500"></div>
                  <span className="text-muted-foreground">Appointments</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-emerald-500"></div>
                  <span className="text-muted-foreground">Sessions</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {adminCalendarMode === "month" && (<>
            {/* Calendar Navigation */}
            <div className="flex items-center justify-between mb-4">
              <Button variant="outline" size="sm" onClick={() => setAdminCalendarMonth(subMonths(adminCalendarMonth, 1))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h3 className="font-semibold text-lg">
                {format(adminCalendarMonth, "MMMM yyyy")}
              </h3>
              <Button variant="outline" size="sm" onClick={() => setAdminCalendarMonth(addMonths(adminCalendarMonth, 1))}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Day headers */}
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                  {day}
                </div>
              ))}
              
              {/* Calendar days */}
              {(() => {
                const monthStart = startOfMonth(adminCalendarMonth);
                const monthEnd = endOfMonth(adminCalendarMonth);
                const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
                const startDayOfWeek = monthStart.getDay();
                const emptyCells = Array(startDayOfWeek).fill(null);
                
                return [...emptyCells, ...days].map((day, index) => {
                  if (!day) {
                    return <div key={`empty-${index}`} className="h-28 border rounded-md bg-muted/20" />;
                  }
                  
                  const dayBlocks = adminCalendarData.availability_blocks?.filter((b) => {
                    const blockDate = new Date(b.start_time);
                    return isSameDay(blockDate, day);
                  }) || [];
                  
                  const dayAppointments = adminCalendarData.appointments?.filter((a) => {
                    const apptDate = new Date(a.window_start);
                    return isSameDay(apptDate, day);
                  }) || [];
                  
                  const daySessions = adminCalendarData.sessions?.filter((s) => {
                    const sessionDate = new Date(s.start_time);
                    return isSameDay(sessionDate, day);
                  }) || [];
                  
                  const isToday = isSameDay(day, new Date());
                  const isSelected = selectedAdminCalendarDate && isSameDay(day, selectedAdminCalendarDate);
                  
                  return (
                    <div
                      key={day.toISOString()}
                      data-testid={`admin-cal-day-${format(day, "yyyy-MM-dd")}`}
                      className={`h-28 border rounded-md p-1 cursor-pointer hover:bg-muted/50 transition-colors overflow-hidden ${
                        isToday ? "border-primary border-2" : ""
                      } ${isSelected ? "bg-primary/10" : ""}`}
                      onClick={() => setSelectedAdminCalendarDate(day)}
                    >
                      <div className={`text-sm font-medium mb-1 ${isToday ? "text-primary" : ""}`}>
                        {format(day, "d")}
                      </div>
                      <div className="space-y-0.5 overflow-hidden">
                        {dayBlocks.slice(0, 1).map((block) => (
                          <div
                            key={block.id}
                            className="text-xs px-1 py-0.5 rounded bg-blue-500 text-white truncate"
                            title={`${block.therapist_name}: ${new Date(block.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${new Date(block.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                          >
                            {block.therapist_name?.split(' ')[0]}
                          </div>
                        ))}
                        {dayBlocks.length > 1 && (
                          <div className="text-xs text-blue-600">+{dayBlocks.length - 1} more</div>
                        )}
                        {dayAppointments.slice(0, 1).map((appt) => (
                          <div
                            key={appt.id}
                            className="text-xs px-1 py-0.5 rounded bg-red-500 text-white truncate"
                            title={`${appt.patient_name} - ${appt.therapist_name || 'TBD'}`}
                          >
                            {appt.patient_name?.split(' ')[0]}
                          </div>
                        ))}
                        {dayAppointments.length > 1 && (
                          <div className="text-xs text-red-600">+{dayAppointments.length - 1} appts</div>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            {/* Selected Day Details */}
            {selectedAdminCalendarDate && (
              <div className="mt-4 p-4 border rounded-lg bg-muted/20">
                <h4 className="font-semibold mb-3">
                  {format(selectedAdminCalendarDate, "EEEE, MMMM d, yyyy")}
                </h4>

                {/* Free time per therapist (user request) */}
                <div className="mb-4" data-testid="admin-day-free-time-section">
                  <h5 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-emerald-500"></div>
                    Free appointment time on {format(selectedAdminCalendarDate, "EEE MMM d")}
                  </h5>
                  {(() => {
                    const dayBlocks = (adminCalendarData.availability_blocks || []).filter((b) =>
                      isSameDay(new Date(b.start_time), selectedAdminCalendarDate)
                      && b.status !== "released" && b.status !== "canceled"
                    );
                    const dayAppts = (adminCalendarData.appointments || []).filter((a) =>
                      isSameDay(new Date(a.window_start), selectedAdminCalendarDate)
                      && a.status !== "canceled"
                    );
                    if (dayBlocks.length === 0) {
                      return <p className="text-sm text-muted-foreground">No therapist availability this day.</p>;
                    }
                    // Aggregate by therapist
                    // For TODAY's view: clamp block/appointment start to "now" so we
                    // measure only time that is actually still available, not past hours.
                    // For past dates: full historical record. For future dates: full forecast.
                    const nowMs = Date.now();
                    const isToday = isSameDay(selectedAdminCalendarDate, new Date());
                    const minutesBetween = (s, e) => {
                      let startMs = new Date(s).getTime();
                      if (isToday) startMs = Math.max(startMs, nowMs);
                      const endMs = new Date(e).getTime();
                      return Math.max(0, Math.round((endMs - startMs) / 60000));
                    };
                    const rows = new Map();
                    for (const b of dayBlocks) {
                      const k = b.therapist_name || "Unassigned";
                      if (!rows.has(k)) rows.set(k, { name: k, blockMin: 0, bookedMin: 0 });
                      rows.get(k).blockMin += minutesBetween(b.start_time, b.end_time);
                    }
                    for (const a of dayAppts) {
                      const k = a.therapist_name || "Unassigned";
                      if (!rows.has(k)) rows.set(k, { name: k, blockMin: 0, bookedMin: 0 });
                      rows.get(k).bookedMin += minutesBetween(a.window_start, a.window_end);
                    }
                    // For today, drop therapists whose remaining block time is 0
                    // (their day is effectively over). For other dates, keep the
                    // full list so historical/forecast views remain complete.
                    const visibleRows = Array.from(rows.values()).filter((r) =>
                      isToday ? r.blockMin > 0 : true
                    );
                    if (isToday && visibleRows.length === 0) {
                      return (
                        <p className="text-sm text-muted-foreground italic">
                          All therapist blocks for today have ended.
                        </p>
                      );
                    }
                    const fmtMin = (m) => {
                      if (m <= 0) return isToday ? "no more free time today" : "fully booked";
                      const h = Math.floor(m / 60), r = m % 60;
                      if (h && r) return `${h}h ${r}m free`;
                      if (h) return `${h}h free`;
                      return `${r}m free`;
                    };
                    const sorted = visibleRows.sort((a, b) =>
                      (b.blockMin - b.bookedMin) - (a.blockMin - a.bookedMin)
                    );
                    return (
                      <div className="space-y-1" data-testid="admin-day-free-time">
                        {sorted.map((row) => {
                          const free = row.blockMin - row.bookedMin;
                          const pct = row.blockMin > 0 ? Math.round((free / row.blockMin) * 100) : 0;
                          const colorClass =
                            free <= 0 ? "bg-red-50 border-red-200" :
                            pct >= 50 ? "bg-emerald-50 border-emerald-200" :
                            "bg-amber-50 border-amber-200";
                          return (
                            <div
                              key={row.name}
                              className={`text-sm flex items-center justify-between gap-2 p-2 border rounded ${colorClass}`}
                              data-testid={`admin-free-row-${row.name}`}
                            >
                              <span className="font-medium">{row.name}</span>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-muted-foreground">
                                  {Math.round(row.blockMin / 60 * 10) / 10}h scheduled • {Math.round(row.bookedMin / 60 * 10) / 10}h booked
                                </span>
                                <Badge className={
                                  free <= 0 ? "bg-red-100 text-red-700" :
                                  pct >= 50 ? "bg-emerald-100 text-emerald-700" :
                                  "bg-amber-100 text-amber-700"
                                }>
                                  {fmtMin(free)}
                                </Badge>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Availability Blocks */}
                <div className="mb-4">
                  <h5 className="text-sm font-medium text-blue-700 mb-2 flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-blue-500"></div>
                    Therapist Availability
                  </h5>
                  {(() => {
                    const dayBlocks = adminCalendarData.availability_blocks?.filter((b) => 
                      isSameDay(new Date(b.start_time), selectedAdminCalendarDate)
                    ) || [];
                    if (dayBlocks.length === 0) {
                      return <p className="text-sm text-muted-foreground">No availability blocks</p>;
                    }
                    return (
                      <div className="space-y-1">
                        {dayBlocks.map((block) => (
                          <div key={block.id} className="text-sm flex items-center gap-2 p-2 bg-blue-50 rounded">
                            <span className="font-medium">{block.therapist_name}</span>
                            <span className="text-muted-foreground">
                              {new Date(block.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - 
                              {new Date(block.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <Badge variant="outline" className="text-xs">{block.status}</Badge>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Appointments */}
                <div>
                  <h5 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-red-500"></div>
                    Scheduled Appointments
                  </h5>
                  {(() => {
                    const dayAppointments = adminCalendarData.appointments?.filter((a) => 
                      isSameDay(new Date(a.window_start), selectedAdminCalendarDate)
                    ) || [];
                    if (dayAppointments.length === 0) {
                      return <p className="text-sm text-muted-foreground">No appointments scheduled</p>;
                    }
                    return (
                      <div className="space-y-1">
                        {dayAppointments.map((appt) => (
                          <div key={appt.id} className="text-sm flex items-center gap-2 p-2 bg-red-50 rounded">
                            <span className="font-medium">{appt.patient_name}</span>
                            <span className="text-muted-foreground">
                              {new Date(appt.window_start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - 
                              {new Date(appt.window_end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <Badge variant="outline" className="text-xs">{appt.status}</Badge>
                            {appt.therapist_name && (
                              <span className="text-xs text-muted-foreground">({appt.therapist_name})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
            </>)}

            {/* Req #2: Day View — stacked list of all therapists for a chosen date */}
            {adminCalendarMode === "day" && (
              <div className="space-y-4" data-testid="admin-day-view">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const prev = new Date(adminDayViewDate);
                      prev.setDate(prev.getDate() - 1);
                      setAdminDayViewDate(prev);
                      setAdminCalendarMonth(new Date(prev.getFullYear(), prev.getMonth(), 1));
                    }}
                    data-testid="admin-day-prev"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div className="flex items-center gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="font-semibold" data-testid="admin-day-picker">
                          <Calendar className="w-4 h-4 mr-2" />
                          {format(adminDayViewDate, "EEEE, MMMM d, yyyy")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto p-0"
                        align="center"
                        onOpenAutoFocus={(e) => e.preventDefault()}
                      >
                        <CalendarPicker
                          mode="single"
                          selected={adminDayViewDate}
                          onSelect={(d) => {
                            if (!d) return;
                            setAdminDayViewDate(d);
                            setAdminCalendarMonth(new Date(d.getFullYear(), d.getMonth(), 1));
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const today = new Date();
                        setAdminDayViewDate(today);
                        setAdminCalendarMonth(new Date(today.getFullYear(), today.getMonth(), 1));
                      }}
                      data-testid="admin-day-today"
                    >
                      Today
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const next = new Date(adminDayViewDate);
                      next.setDate(next.getDate() + 1);
                      setAdminDayViewDate(next);
                      setAdminCalendarMonth(new Date(next.getFullYear(), next.getMonth(), 1));
                    }}
                    data-testid="admin-day-next"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                {(() => {
                  const dayBlocks = (adminCalendarData.availability_blocks || []).filter((b) =>
                    isSameDay(new Date(b.start_time), adminDayViewDate)
                  );
                  const dayAppointments = (adminCalendarData.appointments || []).filter((a) =>
                    isSameDay(new Date(a.window_start), adminDayViewDate)
                  );
                  const daySessions = (adminCalendarData.sessions || []).filter((s) =>
                    isSameDay(new Date(s.start_time), adminDayViewDate)
                  );

                  // Group everything by therapist name so leadership can scan one row per therapist.
                  const therapists = new Map();
                  const keyFor = (name) => name || "Unassigned";

                  for (const b of dayBlocks) {
                    const k = keyFor(b.therapist_name);
                    if (!therapists.has(k)) therapists.set(k, { name: k, blocks: [], appointments: [], sessions: [] });
                    therapists.get(k).blocks.push(b);
                  }
                  for (const a of dayAppointments) {
                    const k = keyFor(a.therapist_name);
                    if (!therapists.has(k)) therapists.set(k, { name: k, blocks: [], appointments: [], sessions: [] });
                    therapists.get(k).appointments.push(a);
                  }
                  for (const s of daySessions) {
                    const k = keyFor(s.therapist_name);
                    if (!therapists.has(k)) therapists.set(k, { name: k, blocks: [], appointments: [], sessions: [] });
                    therapists.get(k).sessions.push(s);
                  }

                  const rows = Array.from(therapists.values()).sort((a, b) => a.name.localeCompare(b.name));

                  if (rows.length === 0) {
                    return (
                      <div className="text-center py-12 text-muted-foreground border rounded-md">
                        <Calendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
                        <p>No availability, appointments, or sessions on this day.</p>
                      </div>
                    );
                  }

                  const fmtT = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

                  return (
                    <div className="space-y-3" data-testid="admin-day-list">
                      <div className="text-xs text-muted-foreground">
                        {rows.length} therapist{rows.length === 1 ? "" : "s"} active •{" "}
                        {dayBlocks.length} block{dayBlocks.length === 1 ? "" : "s"} •{" "}
                        {dayAppointments.length} appt{dayAppointments.length === 1 ? "" : "s"} •{" "}
                        {daySessions.length} session{daySessions.length === 1 ? "" : "s"}
                      </div>
                      {(() => {
                        // For TODAY: clamp block/appt start to "now" so free-time
                        // doesn't include hours that have already passed.
                        const nowMs = Date.now();
                        const isToday = isSameDay(adminDayViewDate, new Date());
                        const minBetween = (s, e) => {
                          let startMs = new Date(s).getTime();
                          if (isToday) startMs = Math.max(startMs, nowMs);
                          const endMs = new Date(e).getTime();
                          return Math.max(0, Math.round((endMs - startMs) / 60000));
                        };
                        return rows.map((row) => {
                          const blockMin = row.blocks
                            .filter(b => b.status !== "released" && b.status !== "canceled")
                            .reduce((acc, b) => acc + minBetween(b.start_time, b.end_time), 0);
                          const bookedMin = row.appointments
                            .filter(a => a.status !== "canceled")
                            .reduce((acc, a) => acc + minBetween(a.window_start, a.window_end), 0);
                          const freeMin = blockMin - bookedMin;
                          const fmtFree = (m) => {
                            if (blockMin <= 0) return isToday ? "day has ended" : null;
                          if (m <= 0) return "fully booked";
                          const h = Math.floor(m / 60), r = m % 60;
                          if (h && r) return `${h}h ${r}m free`;
                          if (h) return `${h}h free`;
                          return `${r}m free`;
                        };
                        const freeLabel = fmtFree(freeMin);
                        const freeCls =
                          freeMin <= 0 ? "bg-red-100 text-red-700" :
                          (blockMin > 0 && freeMin / blockMin >= 0.5) ? "bg-emerald-100 text-emerald-700" :
                          "bg-amber-100 text-amber-700";
                        return (
                        <div key={row.name} className="border rounded-lg p-3 space-y-2" data-testid={`admin-day-row-${row.name}`}>
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <h4 className="font-semibold">{row.name}</h4>
                            <div className="flex gap-1 text-xs flex-wrap">
                              {freeLabel && (
                                <Badge className={freeCls} data-testid={`admin-day-free-${row.name}`}>
                                  {freeLabel}
                                </Badge>
                              )}
                              {row.blocks.length > 0 && <Badge className="bg-blue-100 text-blue-700">{row.blocks.length} block{row.blocks.length === 1 ? "" : "s"}</Badge>}
                              {row.appointments.length > 0 && <Badge className="bg-red-100 text-red-700">{row.appointments.length} appt{row.appointments.length === 1 ? "" : "s"}</Badge>}
                              {row.sessions.length > 0 && <Badge className="bg-emerald-100 text-emerald-700">{row.sessions.length} session{row.sessions.length === 1 ? "" : "s"}</Badge>}
                            </div>
                          </div>
                          {row.blocks.length > 0 && (
                            <div className="space-y-1">
                              {row.blocks.map((b) => (
                                <div key={b.id} className="text-sm flex items-center gap-2 p-2 bg-blue-50 rounded">
                                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                                  <span>{fmtT(b.start_time)} – {fmtT(b.end_time)}</span>
                                  <Badge variant="outline" className="text-xs">{b.status}</Badge>
                                  {b.specialty && <span className="text-xs text-muted-foreground">{b.specialty}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                          {row.appointments.length > 0 && (
                            <div className="space-y-1">
                              {row.appointments.map((a) => (
                                <div key={a.id} className="text-sm flex items-center gap-2 p-2 bg-red-50 rounded flex-wrap">
                                  <div className="w-2 h-2 rounded-full bg-red-500" />
                                  <span>{fmtT(a.window_start)} – {fmtT(a.window_end)}</span>
                                  <span className="font-medium">{a.patient_name}</span>
                                  {a.visit_type && <Badge variant="outline" className="text-xs">{a.visit_type}</Badge>}
                                  {a.status && <Badge variant="outline" className="text-xs">{a.status}</Badge>}
                                  {a.location && <span className="text-xs text-muted-foreground">{a.location}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                          {row.sessions.length > 0 && (
                            <div className="space-y-1">
                              {row.sessions.map((s) => (
                                <div key={s.id} className="text-sm flex items-center gap-2 p-2 bg-emerald-50 rounded">
                                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                  <span>{fmtT(s.start_time)}</span>
                                  <span className="font-medium">{s.patient_name}</span>
                                  {s.outcome && <Badge variant="outline" className="text-xs">{s.outcome}</Badge>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        );
                      });
                      })()}
                    </div>
                  );
                })()}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Therapist Status with Colors - Collapsible */}
        <Card>
          <CardHeader 
            className="cursor-pointer hover:bg-muted/50 transition-colors" 
            onClick={() => setTherapistStatusExpanded(!therapistStatusExpanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Therapist Availability Status
                </CardTitle>
                <Badge variant="outline" className="text-xs">
                  {therapistStatuses.length} therapist{therapistStatuses.length !== 1 ? 's' : ''}
                </Badge>
                {therapistStatuses.filter(t => t.status_color === "green").length > 0 && (
                  <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
                    {therapistStatuses.filter(t => t.status_color === "green").length} active
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-4">
                {therapistStatusExpanded && (
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <Circle className="w-3 h-3 fill-green-500 text-green-500" />
                      <span className="text-muted-foreground">Active</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Circle className="w-3 h-3 fill-orange-500 text-orange-500" />
                      <span className="text-muted-foreground">Paused</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Circle className="w-3 h-3 fill-blue-500 text-blue-500" />
                      <span className="text-muted-foreground">Scheduled</span>
                    </div>
                  </div>
                )}
                {therapistStatusExpanded ? (
                  <ChevronUp className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
            </div>
            {!therapistStatusExpanded && (
              <CardDescription className="mt-1">
                Click to expand • {therapistStatuses.filter(t => t.status_color === "green").length} active, {therapistStatuses.filter(t => t.status_color === "orange").length} paused, {therapistStatuses.filter(t => t.status_color === "blue").length} scheduled
              </CardDescription>
            )}
          </CardHeader>
          {therapistStatusExpanded && (
            <CardContent>
            {therapistStatuses.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No therapists with availability today</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {therapistStatuses.map((therapist) => (
                  <div
                    key={therapist.id}
                    className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                      therapist.status_color === "green" ? "border-green-300 bg-green-50/50" :
                      therapist.status_color === "orange" ? "border-orange-300 bg-orange-50/50" :
                      therapist.status_color === "blue" ? "border-blue-300 bg-blue-50/50" :
                      "border-gray-200"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${
                        therapist.status_color === "green" ? "bg-green-500" :
                        therapist.status_color === "orange" ? "bg-orange-500" :
                        therapist.status_color === "blue" ? "bg-blue-500" :
                        "bg-gray-400"
                      }`} />
                      <div>
                        <p className="font-medium">{therapist.name}</p>
                        <p className="text-sm text-muted-foreground">{therapist.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <Badge variant="outline" className={`${
                          therapist.status_color === "green" ? "border-green-500 text-green-700" :
                          therapist.status_color === "orange" ? "border-orange-500 text-orange-700" :
                          therapist.status_color === "blue" ? "border-blue-500 text-blue-700" :
                          ""
                        }`}>
                          {therapist.status_label}
                        </Badge>
                        {therapist.active_sessions > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {therapist.active_sessions} active session(s)
                          </p>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedTherapistForCalendar(therapist.id);
                          setTherapistCalendarMonth(new Date());
                        }}
                      >
                        <Calendar className="w-4 h-4 mr-1" />
                        Calendar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
          )}
        </Card>

        {/* Therapist Calendar View Dialog */}
        <Dialog open={!!selectedTherapistForCalendar} onOpenChange={(open) => !open && setSelectedTherapistForCalendar(null)}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>
                {therapistCalendarData?.therapist?.name || "Therapist"}&apos;s Calendar
              </DialogTitle>
              <DialogDescription>
                View availability and booked sessions
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Calendar Navigation */}
              <div className="flex items-center justify-between">
                <Button variant="outline" size="sm" onClick={() => setTherapistCalendarMonth(subMonths(therapistCalendarMonth, 1))}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <h3 className="font-semibold text-lg">
                  {format(therapistCalendarMonth, "MMMM yyyy")}
                </h3>
                <Button variant="outline" size="sm" onClick={() => setTherapistCalendarMonth(addMonths(therapistCalendarMonth, 1))}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-blue-500" />
                  <span>Availability</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-red-500" />
                  <span>Sessions</span>
                </div>
              </div>

              {/* Calendar Grid */}
              {therapistCalendarData && (
                <div className="grid grid-cols-7 gap-1">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                      {day}
                    </div>
                  ))}
                  
                  {(() => {
                    const monthStart = startOfMonth(therapistCalendarMonth);
                    const monthEnd = endOfMonth(therapistCalendarMonth);
                    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
                    const startDayOfWeek = monthStart.getDay();
                    const emptyCells = Array(startDayOfWeek).fill(null);
                    
                    return [...emptyCells, ...days].map((day, index) => {
                      if (!day) {
                        return <div key={`empty-${index}`} className="h-20 border rounded-md bg-muted/20" />;
                      }
                      
                      const dayBlocks = therapistCalendarData.availability_blocks?.filter((b) => {
                        const blockDate = new Date(b.start_time);
                        return isSameDay(blockDate, day);
                      }) || [];
                      
                      const daySessions = therapistCalendarData.sessions?.filter((s) => {
                        const sessionDate = new Date(s.start_time);
                        return isSameDay(sessionDate, day);
                      }) || [];
                      
                      const isToday = isSameDay(day, new Date());
                      
                      return (
                        <div
                          key={day.toISOString()}
                          className={`h-20 border rounded-md p-1 overflow-hidden ${isToday ? "border-primary border-2" : ""}`}
                        >
                          <div className={`text-sm font-medium mb-1 ${isToday ? "text-primary" : ""}`}>
                            {format(day, "d")}
                          </div>
                          <div className="space-y-0.5">
                            {dayBlocks.slice(0, 2).map((block) => {
                              const start = new Date(block.start_time);
                              const end = new Date(block.end_time);
                              const startH = start.getHours();
                              const endH = end.getHours();
                              const timeStr = `${startH > 12 ? startH - 12 : startH}-${endH > 12 ? endH - 12 : endH}`;
                              return (
                                <div key={block.id} className="text-xs px-1 py-0.5 rounded bg-blue-500 text-white truncate">
                                  {timeStr}
                                </div>
                              );
                            })}
                            {daySessions.slice(0, 1).map((session) => {
                              const duration = session.duration_minutes || '';
                              return (
                                <div key={session.id} className="text-xs px-1 py-0.5 rounded bg-red-500 text-white truncate">
                                  {session.patient_name?.split(" ")[0]}{duration ? ` (${duration}m)` : ''}
                                </div>
                              );
                            })}
                            {(dayBlocks.length > 2 || daySessions.length > 1) && (
                              <div className="text-xs text-muted-foreground">+more</div>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Tabs for different sections */}
        <Tabs defaultValue="appointments" className="space-y-6">
          <TabsList className="flex-wrap">
            <TabsTrigger value="appointments">All Appointments</TabsTrigger>
            <TabsTrigger value="therapists">Therapists</TabsTrigger>
            <TabsTrigger value="users">
              <UserCog className="w-4 h-4 mr-2" />
              User Management
            </TabsTrigger>
            <TabsTrigger value="waitlist">
              <Clock className="w-4 h-4 mr-2" />
              Waitlist
              {waitlist.filter(w => w.status === "pending").length > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 px-1.5">
                  {waitlist.filter(w => w.status === "pending").length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="session-history">
              <History className="w-4 h-4 mr-2" />
              Session History
            </TabsTrigger>
            <TabsTrigger value="facilitator-actions">
              <ClipboardList className="w-4 h-4 mr-2" />
              Facilitator Actions
            </TabsTrigger>
            <TabsTrigger value="email-logs">
              <Mail className="w-4 h-4 mr-2" />
              Email Logs
            </TabsTrigger>
            <TabsTrigger value="whats-new" data-testid="tab-whats-new">
              <Sparkles className="w-4 h-4 mr-2" />
              What's New
            </TabsTrigger>
            <TabsTrigger value="session-audits">
              <FileText className="w-4 h-4 mr-2" />
              Session Audits
            </TabsTrigger>
            <TabsTrigger value="impersonation" data-testid="tab-impersonation">
              <UserCog className="w-4 h-4 mr-2" />
              Impersonation
            </TabsTrigger>
          </TabsList>

          {/* All Appointments Tab */}
          <TabsContent value="appointments">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope']">All Appointments</CardTitle>
                <CardDescription>Complete appointment history with booking details</CardDescription>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by patient name, status, discipline, location..."
                    value={appointmentSearch}
                    onChange={(e) => setAppointmentSearch(e.target.value)}
                    className="pl-9"
                    data-testid="appointment-search"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {allAppointments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No appointments yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {allAppointments
                      .filter((appt) => {
                        if (!appointmentSearch) return true;
                        const search = appointmentSearch.toLowerCase();
                        return (
                          appt.patient_name?.toLowerCase().includes(search) ||
                          appt.status?.toLowerCase().includes(search) ||
                          appt.visit_type?.toLowerCase().includes(search) ||
                          appt.required_discipline?.toLowerCase().includes(search) ||
                          appt.state?.toLowerCase().includes(search) ||
                          appt.location?.toLowerCase().includes(search) ||
                          appt.booked_by_name?.toLowerCase().includes(search) ||
                          appt.patient_email?.toLowerCase().includes(search)
                        );
                      })
                      .sort((a, b) => new Date(b.window_start) - new Date(a.window_start))
                      .map((appt) => (
                      <div
                        key={appt.id}
                        className="flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-lg gap-4"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">{appt.patient_name}</p>
                            <Badge
                              className={
                                appt.status === "completed"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : appt.status === "canceled"
                                  ? "bg-red-100 text-red-700"
                                  : appt.status === "scheduled"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-amber-100 text-amber-700"
                              }
                            >
                              {appt.status}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                            <span>{appt.visit_type}</span>
                            <span>•</span>
                            <span>{appt.required_discipline}</span>
                            <span>•</span>
                            <span>{appt.state}</span>
                            <span>•</span>
                            <span>{appt.location}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {new Date(appt.window_start).toLocaleDateString()} at{" "}
                            {new Date(appt.window_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            {" - "}
                            {new Date(appt.window_end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </div>
                        <div className="text-right">
                          {appt.booked_by_name ? (
                            <div className="text-sm">
                              <span className="text-muted-foreground">Booked by: </span>
                              <span className="font-medium">{appt.booked_by_name}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">Legacy booking</span>
                          )}
                          {appt.patient_email && (
                            <p className="text-xs text-muted-foreground">{appt.patient_email}</p>
                          )}
                        </div>
                      </div>
                    ))}
                    {allAppointments.filter((appt) => {
                      if (!appointmentSearch) return true;
                      const search = appointmentSearch.toLowerCase();
                      return (
                        appt.patient_name?.toLowerCase().includes(search) ||
                        appt.status?.toLowerCase().includes(search) ||
                        appt.visit_type?.toLowerCase().includes(search) ||
                        appt.required_discipline?.toLowerCase().includes(search) ||
                        appt.state?.toLowerCase().includes(search) ||
                        appt.location?.toLowerCase().includes(search) ||
                        appt.booked_by_name?.toLowerCase().includes(search) ||
                        appt.patient_email?.toLowerCase().includes(search)
                      );
                    }).length === 0 && appointmentSearch && (
                      <div className="text-center py-8 text-muted-foreground">
                        <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No appointments match your search</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Therapist Overview Tab */}
          <TabsContent value="therapists">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle className="font-['Manrope']">Therapist Overview</CardTitle>
                    <CardDescription>
                      Current status of all therapists. Phone numbers appear on the facilitator&apos;s appointment card and confirmation email.
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSyncTherapistNames}
                    disabled={syncingNames}
                    data-testid="sync-therapist-names-btn"
                  >
                    {syncingNames ? "Syncing..." : "Sync names from Microsoft"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {therapists.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No therapists registered yet</p>
                    <p className="text-sm mt-2">Therapists will appear here after they log in via SSO</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {therapists.map((therapist) => (
                      <div
                        key={therapist.id}
                        className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            {therapist.name?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium">{therapist.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {therapist.specialty} • {therapist.states_covered?.slice(0, 2).join(", ")}
                              {therapist.states_covered?.length > 2 && ` +${therapist.states_covered.length - 2} more`}
                            </p>
                            {therapist.phone && (
                              <p className="text-xs text-blue-600">{therapist.phone}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Input
                            type="tel"
                            className="w-44"
                            placeholder="Phone number"
                            value={phoneDrafts[therapist.id] ?? therapist.phone ?? ""}
                            onChange={(e) =>
                              setPhoneDrafts({ ...phoneDrafts, [therapist.id]: e.target.value })
                            }
                            data-testid={`therapist-phone-input-${therapist.id}`}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={
                              phoneDrafts[therapist.id] === undefined || savingPhoneId === therapist.id
                            }
                            onClick={() => handleSaveTherapistPhone(therapist.id)}
                            data-testid={`save-therapist-phone-${therapist.id}`}
                          >
                            {savingPhoneId === therapist.id ? "Saving..." : "Save"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingTherapist({ ...therapist })}
                            data-testid={`edit-therapist-${therapist.id}`}
                          >
                            Edit profile
                          </Button>
                          <Badge
                            className={
                              therapist.status === "available"
                                ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                                : therapist.status === "busy"
                                ? "bg-amber-100 text-amber-700 border-amber-200"
                                : "bg-slate-100 text-slate-700 border-slate-200"
                            }
                          >
                            {therapist.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Dialog open={!!editingTherapist} onOpenChange={(open) => !open && setEditingTherapist(null)}>
              <DialogContent className="max-w-md" data-testid="edit-therapist-dialog">
                <DialogHeader>
                  <DialogTitle>Edit therapist profile</DialogTitle>
                  <DialogDescription>
                    Changes apply across TeleTech — dashboards, booking slots and confirmation emails.
                    A name set here won&apos;t be overwritten by Microsoft on their next login.
                  </DialogDescription>
                </DialogHeader>
                {editingTherapist && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-therapist-name">Display name</Label>
                      <Input
                        id="edit-therapist-name"
                        value={editingTherapist.name || ""}
                        onChange={(e) => setEditingTherapist({ ...editingTherapist, name: e.target.value })}
                        data-testid="edit-therapist-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-therapist-phone">Contact phone</Label>
                      <Input
                        id="edit-therapist-phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={editingTherapist.phone || ""}
                        onChange={(e) => setEditingTherapist({ ...editingTherapist, phone: e.target.value })}
                        data-testid="edit-therapist-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Specialty</Label>
                      <Select
                        value={editingTherapist.specialty || ""}
                        onValueChange={(val) => setEditingTherapist({ ...editingTherapist, specialty: val })}
                      >
                        <SelectTrigger data-testid="edit-therapist-specialty">
                          <SelectValue placeholder="Select specialty" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PT">Physical Therapy (PT)</SelectItem>
                          <SelectItem value="OT">Occupational Therapy (OT)</SelectItem>
                          <SelectItem value="SLP">Speech Language Pathology (SLP)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-therapist-max">Max concurrent sessions</Label>
                      <Input
                        id="edit-therapist-max"
                        type="number"
                        min="1"
                        value={editingTherapist.max_concurrent || 1}
                        onChange={(e) => setEditingTherapist({ ...editingTherapist, max_concurrent: e.target.value })}
                        data-testid="edit-therapist-max-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Email: {editingTherapist.email} — licensed states are managed by the therapist in their own License Settings.
                    </p>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditingTherapist(null)}>Cancel</Button>
                  <Button
                    onClick={handleSaveTherapistProfile}
                    disabled={savingTherapist || !editingTherapist?.name?.trim()}
                    data-testid="save-therapist-profile-btn"
                  >
                    {savingTherapist ? "Saving..." : "Save changes"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* User Management Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-['Manrope'] flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      User Management
                    </CardTitle>
                    <CardDescription>
                      Manage user roles and access. Pre-register users to set their role before they log in.
                    </CardDescription>
                  </div>
                  <Dialog open={preRegisterDialogOpen} onOpenChange={setPreRegisterDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="gap-2" data-testid="pre-register-user-btn">
                        <UserPlus className="w-4 h-4" />
                        Pre-Register User
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Pre-Register User</DialogTitle>
                        <DialogDescription>
                          Add a user by email before they log in. When they sign in via Microsoft SSO, 
                          they will automatically be assigned the role you specify here.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address</Label>
                          <div className="flex gap-2">
                            <Input
                              id="email"
                              type="email"
                              placeholder="user@therapymgmt.com"
                              value={preRegisterEmail}
                              onChange={(e) => setPreRegisterEmail(e.target.value)}
                              data-testid="pre-register-email"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Only @therapymgmt.com email addresses can be pre-registered
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="name">Name (Optional)</Label>
                          <Input
                            id="name"
                            placeholder="John Doe"
                            value={preRegisterName}
                            onChange={(e) => setPreRegisterName(e.target.value)}
                            data-testid="pre-register-name"
                          />
                          <p className="text-xs text-muted-foreground">
                            If left blank, name will be derived from their Microsoft account
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label>Roles (select one or more)</Label>
                          <div className="space-y-3 pt-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox 
                                id="role-therapist"
                                checked={preRegisterRoles.includes("therapist")}
                                onCheckedChange={(checked) => {
                                  setPreRegisterRoles(prev => 
                                    checked ? [...prev, "therapist"] : prev.filter(r => r !== "therapist")
                                  );
                                }}
                                data-testid="pre-register-role-therapist"
                              />
                              <label htmlFor="role-therapist" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                Therapist
                              </label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox 
                                id="role-facilitator"
                                checked={preRegisterRoles.includes("facilitator")}
                                onCheckedChange={(checked) => {
                                  setPreRegisterRoles(prev => 
                                    checked ? [...prev, "facilitator"] : prev.filter(r => r !== "facilitator")
                                  );
                                }}
                                data-testid="pre-register-role-facilitator"
                              />
                              <label htmlFor="role-facilitator" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                Facilitator
                              </label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox 
                                id="role-admin"
                                checked={preRegisterRoles.includes("admin")}
                                onCheckedChange={(checked) => {
                                  setPreRegisterRoles(prev => 
                                    checked ? [...prev, "admin"] : prev.filter(r => r !== "admin")
                                  );
                                }}
                                data-testid="pre-register-role-admin"
                              />
                              <label htmlFor="role-admin" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                Admin
                              </label>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Users with multiple roles can access all corresponding dashboards
                          </p>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setPreRegisterDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button 
                          onClick={handlePreRegister} 
                          disabled={preRegistering || !preRegisterEmail || preRegisterRoles.length === 0}
                          data-testid="confirm-pre-register"
                        >
                          {preRegistering ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Registering...
                            </>
                          ) : (
                            <>
                              <UserPlus className="w-4 h-4 mr-2" />
                              Pre-Register
                            </>
                          )}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {allUsers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No users yet. Pre-register users or wait for them to sign in via Microsoft SSO.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Pre-Registered Users (Not Yet Logged In) */}
                    {allUsers.filter(u => u.is_pre_registered).length > 0 && (
                      <div className="mb-4">
                        <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Pre-Registered (Pending First Login) ({allUsers.filter(u => u.is_pre_registered).length})
                        </h3>
                        <div className="grid gap-3">
                          {allUsers
                            .filter(u => u.is_pre_registered)
                            .map((u) => (
                              <div
                                key={u.id}
                                className="flex items-center justify-between p-4 border rounded-lg border-dashed border-blue-300 bg-blue-50/50"
                              >
                                <div className="flex items-center gap-4">
                                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-medium text-blue-700">
                                    {u.name?.charAt(0) || '?'}
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium">{u.name || u.email.split('@')[0]}</p>
                                      <Badge className="bg-blue-100 text-blue-700 text-xs">Pending Login</Badge>
                                    </div>
                                    <p className="text-sm text-muted-foreground">{u.email}</p>
                                    <p className="text-xs text-muted-foreground">
                                      Pre-registered: {u.pre_registered_at ? new Date(u.pre_registered_at).toLocaleString() : 'Unknown'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="flex gap-1 flex-wrap">
                                    {getUserRoles(u).map(role => (
                                      <Badge key={role} variant="outline" className="capitalize">{role}</Badge>
                                    ))}
                                  </div>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-red-600 hover:text-red-700"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Delete Pre-Registered User?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          This will remove {u.email} from the pre-registration list. 
                                          They can still log in later but will get the default role instead.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction 
                                          onClick={() => handleDeletePreRegistered(u.id, u.email)}
                                          className="bg-red-600 hover:bg-red-700"
                                        >
                                          Delete
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Active SSO Users */}
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-muted-foreground mb-3">
                        SSO Users ({allUsers.filter(u => u.auth_method === 'sso' && !u.is_pre_registered).length})
                      </h3>
                      <div className="grid gap-3">
                        {allUsers
                          .filter(u => u.auth_method === 'sso' && !u.is_pre_registered)
                          .map((u) => {
                            const userRoles = getUserRoles(u);
                            return (
                            <div
                              key={u.id}
                              className={`flex items-center justify-between p-4 border rounded-lg ${!u.is_active ? 'opacity-50 bg-muted/30' : ''}`}
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-medium">
                                  {u.name?.charAt(0) || '?'}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium">{u.name}</p>
                                    {u.email === 'tplunkett@therapymgmt.com' && (
                                      <Badge className="bg-amber-100 text-amber-700 text-xs">Primary Admin</Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground">{u.email}</p>
                                  <p className="text-xs text-muted-foreground">
                                    Last login: {u.last_login ? new Date(u.last_login).toLocaleString() : 'Never'}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                {/* Role checkboxes for multi-role assignment */}
                                <div className="flex gap-2 items-center">
                                  {["therapist", "facilitator", "admin"].map(role => (
                                    <div key={role} className="flex items-center space-x-1">
                                      <Checkbox
                                        id={`${u.id}-${role}`}
                                        checked={userRoles.includes(role)}
                                        onCheckedChange={() => toggleUserRole(u.id, role, userRoles)}
                                        disabled={u.email === 'tplunkett@therapymgmt.com'}
                                      />
                                      <label htmlFor={`${u.id}-${role}`} className="text-xs capitalize">
                                        {role}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                                {u.email !== 'tplunkett@therapymgmt.com' && (
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className={u.is_active ? "text-red-600 hover:text-red-700" : "text-green-600 hover:text-green-700"}
                                      >
                                        {u.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>
                                          {u.is_active ? 'Deactivate' : 'Activate'} User?
                                        </AlertDialogTitle>
                                        <AlertDialogDescription>
                                          {u.is_active
                                            ? `This will prevent ${u.name} from accessing the system.`
                                            : `This will allow ${u.name} to access the system again.`
                                          }
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction onClick={() => handleToggleActive(u.id, u.is_active)}>
                                          {u.is_active ? 'Deactivate' : 'Activate'}
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                )}
                              </div>
                            </div>
                          );})}
                        {allUsers.filter(u => u.auth_method === 'sso' && !u.is_pre_registered).length === 0 && (
                          <p className="text-sm text-muted-foreground py-4 text-center">
                            No SSO users yet. Ask @therapymgmt.com users to sign in with Microsoft.
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Local/Demo Users */}
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-3">
                        Local Users ({allUsers.filter(u => u.auth_method !== 'sso').length})
                      </h3>
                      <div className="grid gap-3">
                        {allUsers
                          .filter(u => u.auth_method !== 'sso')
                          .map((u) => (
                            <div
                              key={u.id}
                              className={`flex items-center justify-between p-4 border rounded-lg ${!u.is_active ? 'opacity-50 bg-muted/30' : ''}`}
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center font-medium">
                                  {u.name?.charAt(0) || '?'}
                                </div>
                                <div>
                                  <p className="font-medium">{u.name}</p>
                                  <p className="text-sm text-muted-foreground">{u.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="flex gap-1 flex-wrap">
                                  {getUserRoles(u).map(role => (
                                    <Badge key={role} variant="outline" className="capitalize">{role}</Badge>
                                  ))}
                                </div>
                                <Badge variant="secondary">{u.auth_method || 'local'}</Badge>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Waitlist Tab */}
          <TabsContent value="waitlist">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Waitlist
                </CardTitle>
                <CardDescription>
                  Patients waiting for available appointments
                </CardDescription>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by patient name, facilitator, state, discipline..."
                    value={waitlistSearch}
                    onChange={(e) => setWaitlistSearch(e.target.value)}
                    className="pl-9"
                    data-testid="waitlist-search"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {waitlist.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No patients on the waitlist</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Patients are added to the waitlist when no appointments are available
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Status Summary */}
                    <div className="flex gap-4 mb-4">
                      <Badge variant="destructive" className="text-sm px-3 py-1">
                        {waitlist.filter(w => w.status === "pending").length} Pending
                      </Badge>
                      <Badge variant="secondary" className="text-sm px-3 py-1">
                        {waitlist.filter(w => w.status === "fulfilled").length} Fulfilled
                      </Badge>
                      <Badge variant="outline" className="text-sm px-3 py-1">
                        {waitlist.filter(w => w.status === "cancelled").length} Cancelled
                      </Badge>
                    </div>
                    
                    {/* Waitlist Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-3 font-medium">Status</th>
                            <th className="text-left p-3 font-medium">Patient</th>
                            <th className="text-left p-3 font-medium">Contact</th>
                            <th className="text-left p-3 font-medium">Discipline</th>
                            <th className="text-left p-3 font-medium">State</th>
                            <th className="text-left p-3 font-medium">Visit Type</th>
                            <th className="text-left p-3 font-medium">Duration</th>
                            <th className="text-left p-3 font-medium">Location</th>
                            <th className="text-left p-3 font-medium">Needed By</th>
                            <th className="text-left p-3 font-medium">Requested By</th>
                            <th className="text-left p-3 font-medium">Requested At</th>
                            <th className="text-left p-3 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {waitlist
                            .filter(entry => {
                              if (!waitlistSearch) return true;
                              const search = waitlistSearch.toLowerCase();
                              return (
                                entry.patient_name?.toLowerCase().includes(search) ||
                                entry.facilitator_name?.toLowerCase().includes(search) ||
                                entry.facilitator_email?.toLowerCase().includes(search) ||
                                entry.state?.toLowerCase().includes(search) ||
                                entry.required_discipline?.toLowerCase().includes(search) ||
                                entry.location?.toLowerCase().includes(search) ||
                                entry.status?.toLowerCase().includes(search)
                              );
                            })
                            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                            .map((entry) => (
                              <tr key={entry.id} className="border-b hover:bg-muted/50">
                                <td className="p-3">
                                  <Badge
                                    variant={
                                      entry.status === "pending" ? "destructive" :
                                      entry.status === "fulfilled" ? "default" : "secondary"
                                    }
                                  >
                                    {entry.status}
                                  </Badge>
                                </td>
                                <td className="p-3 font-medium" data-testid={`waitlist-patient-${entry.id}`}>{entry.patient_name}</td>
                                <td className="p-3 text-sm" data-testid={`waitlist-contact-${entry.id}`}>
                                  {entry.contact_info
                                    ? <span className="text-foreground">{entry.contact_info}</span>
                                    : <span className="text-muted-foreground italic">—</span>}
                                </td>
                                <td className="p-3">
                                  <Badge variant="outline">{entry.required_discipline}</Badge>
                                </td>
                                <td className="p-3">{entry.state}</td>
                                <td className="p-3 text-sm">{entry.visit_type}</td>
                                <td className="p-3">{entry.duration} min</td>
                                <td className="p-3 text-sm">{entry.location}</td>
                                <td className="p-3 text-sm" data-testid={`waitlist-needed-by-${entry.id}`}>
                                  {(() => {
                                    if (!entry.preferred_day) {
                                      return <span className="text-muted-foreground italic">ASAP</span>;
                                    }
                                    // Guard against legacy free-text values like "Monday" — only format if ISO YYYY-MM-DD.
                                    const isoMatch = /^\d{4}-\d{2}-\d{2}$/.test(entry.preferred_day);
                                    if (!isoMatch) {
                                      return <span className="font-medium text-amber-700">{entry.preferred_day}</span>;
                                    }
                                    const d = new Date(entry.preferred_day + "T00:00:00");
                                    if (isNaN(d.getTime())) {
                                      return <span className="font-medium text-amber-700">{entry.preferred_day}</span>;
                                    }
                                    return <span className="font-medium text-amber-700">{format(d, "MMM d, yyyy")}</span>;
                                  })()}
                                </td>
                                <td className="p-3">
                                  <div>
                                    <p className="text-sm font-medium">{entry.facilitator_name}</p>
                                    <p className="text-xs text-muted-foreground">{entry.facilitator_email}</p>
                                  </div>
                                </td>
                                <td className="p-3 text-sm">
                                  {entry.created_at && (
                                    <>
                                      <p>{format(new Date(entry.created_at), "MMM d, yyyy")}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {format(new Date(entry.created_at), "h:mm a")}
                                      </p>
                                    </>
                                  )}
                                </td>
                                <td className="p-3">
                                  {entry.status === "pending" && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                      onClick={() => handleCancelWaitlistEntry(entry.id, entry.patient_name)}
                                      data-testid={`waitlist-remove-${entry.id}`}
                                    >
                                      <Trash2 className="h-4 w-4 mr-1" />
                                      Remove
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {waitlist.filter(entry => {
                      if (!waitlistSearch) return true;
                      const search = waitlistSearch.toLowerCase();
                      return (
                        entry.patient_name?.toLowerCase().includes(search) ||
                        entry.facilitator_name?.toLowerCase().includes(search) ||
                        entry.state?.toLowerCase().includes(search) ||
                        entry.required_discipline?.toLowerCase().includes(search)
                      );
                    }).length === 0 && waitlistSearch && (
                      <div className="text-center py-8">
                        <p className="text-muted-foreground">No results found for "{waitlistSearch}"</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Session History Tab */}
          <TabsContent value="session-history">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Session History
                </CardTitle>
                <CardDescription>
                  View all completed sessions with notes and transcripts
                </CardDescription>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by patient name, therapist name, date..."
                    value={sessionSearch}
                    onChange={(e) => setSessionSearch(e.target.value)}
                    className="pl-9"
                    data-testid="session-search"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {sessions.filter(s => s.outcome === 'completed').length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No completed sessions yet</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {sessions
                      .filter(s => s.outcome === 'completed')
                      .filter((session) => {
                        if (!sessionSearch) return true;
                        const search = sessionSearch.toLowerCase();
                        return (
                          session.patient_name?.toLowerCase().includes(search) ||
                          session.therapist_name?.toLowerCase().includes(search) ||
                          session.pre_session_notes?.toLowerCase().includes(search) ||
                          session.during_session_notes?.toLowerCase().includes(search) ||
                          session.post_session_notes?.toLowerCase().includes(search)
                        );
                      })
                      .sort((a, b) => new Date(b.start_time) - new Date(a.start_time))
                      .map((session) => (
                      <div
                        key={session.id}
                        className="flex flex-col md:flex-row md:items-start justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors gap-4"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold">{session.patient_name}</h3>
                            <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Completed
                            </Badge>
                            {session.transcript && (
                              <Badge variant="outline" className="text-emerald-600">
                                <FileText className="w-3 h-3 mr-1" />
                                Transcript
                              </Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-2">
                            <span>
                              <strong>Therapist:</strong> {session.therapist_name}
                            </span>
                            <span>
                              <strong>Date:</strong> {session.start_time ? new Date(session.start_time).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              }) : 'N/A'}
                            </span>
                            <span>
                              <strong>Time:</strong> {session.start_time ? new Date(session.start_time).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit'
                              }) : 'N/A'}
                            </span>
                          </div>
                          {/* Notes Preview */}
                          {(session.pre_session_notes || session.during_session_notes || session.post_session_notes) && (
                            <div className="mt-2 p-2 bg-muted/50 rounded text-sm">
                              <p className="font-medium text-xs mb-1">Notes Preview:</p>
                              <p className="text-muted-foreground line-clamp-2">
                                {session.post_session_notes || session.during_session_notes || session.pre_session_notes || 'No notes'}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.location.href = `/session/${session.id}`}
                            className="gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                const res = await axios.post(`${API}/sessions/${session.id}/email-summary`);
                                if (res.data.success) {
                                  toast.success(res.data.message);
                                } else {
                                  toast.error(res.data.message);
                                }
                              } catch (error) {
                                toast.error(error.response?.data?.detail || "Failed to send email");
                              }
                            }}
                            className="gap-2"
                          >
                            <Mail className="w-4 h-4" />
                            Email
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Facilitator Actions Tab */}
          <TabsContent value="facilitator-actions">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <ClipboardList className="w-5 h-5" />
                  Facilitator Actions Log
                </CardTitle>
                <CardDescription>
                  Track all actions performed by facilitators including bookings, check-ins, and patient readiness
                </CardDescription>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by facilitator name, email, patient name, action..."
                    value={actionSearch}
                    onChange={(e) => setActionSearch(e.target.value)}
                    className="pl-9"
                    data-testid="action-search"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {facilitatorActions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No facilitator actions recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {[...facilitatorActions]
                      .filter((action) => {
                        if (!actionSearch) return true;
                        const search = actionSearch.toLowerCase();
                        return (
                          action.facilitator_name?.toLowerCase().includes(search) ||
                          action.facilitator_email?.toLowerCase().includes(search) ||
                          action.action_type?.toLowerCase().includes(search) ||
                          action.details?.patient_name?.toLowerCase().includes(search) ||
                          action.details?.discipline?.toLowerCase().includes(search) ||
                          action.details?.state?.toLowerCase().includes(search)
                        );
                      })
                      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                      .map((action) => (
                      <div
                        key={action.id}
                        className="flex items-start justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className={`p-2 rounded-full ${
                            action.action_type === 'appointment_booked' ? 'bg-blue-100 text-blue-600' :
                            action.action_type === 'patient_checked_in' ? 'bg-amber-100 text-amber-600' :
                            action.action_type === 'patient_marked_ready' ? 'bg-emerald-100 text-emerald-600' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {action.action_type === 'appointment_booked' && <Calendar className="w-4 h-4" />}
                            {action.action_type === 'patient_checked_in' && <CheckSquare className="w-4 h-4" />}
                            {action.action_type === 'patient_marked_ready' && <Send className="w-4 h-4" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{action.facilitator_name}</span>
                              <Badge variant="outline" className="text-xs">
                                {action.action_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {action.facilitator_email}
                            </p>
                            {action.details && (
                              <div className="mt-2 text-sm space-y-1">
                                {action.details.patient_name && (
                                  <p><span className="text-muted-foreground">Patient:</span> {action.details.patient_name}</p>
                                )}
                                {action.details.discipline && (
                                  <p><span className="text-muted-foreground">Discipline:</span> {action.details.discipline}</p>
                                )}
                                {action.details.state && (
                                  <p><span className="text-muted-foreground">State:</span> {action.details.state}</p>
                                )}
                                {action.details.visit_type && (
                                  <p><span className="text-muted-foreground">Visit Type:</span> {action.details.visit_type}</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">
                            {new Date(action.timestamp).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(action.timestamp).toLocaleTimeString('en-US', { 
                              hour: 'numeric', 
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Logs Tab */}
          <TabsContent value="email-logs">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Email Logs
                </CardTitle>
                <CardDescription>
                  Track all emails sent by the system including confirmations and notifications
                </CardDescription>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by recipient, subject, status, sender..."
                    value={emailSearch}
                    onChange={(e) => setEmailSearch(e.target.value)}
                    className="pl-9"
                    data-testid="email-search"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {emailLogs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No emails sent yet</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {[...emailLogs]
                      .filter((log) => {
                        if (!emailSearch) return true;
                        const search = emailSearch.toLowerCase();
                        return (
                          log.to_email?.toLowerCase().includes(search) ||
                          log.subject?.toLowerCase().includes(search) ||
                          log.status?.toLowerCase().includes(search) ||
                          log.sent_by_name?.toLowerCase().includes(search) ||
                          log.sent_by_email?.toLowerCase().includes(search) ||
                          log.error?.toLowerCase().includes(search)
                        );
                      })
                      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                      .map((log) => (
                      <div
                        key={log.id}
                        className="flex items-start justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className={`p-2 rounded-full ${
                            log.status === 'sent' ? 'bg-emerald-100 text-emerald-600' :
                            log.status === 'failed' ? 'bg-red-100 text-red-600' :
                            log.status === 'skipped' ? 'bg-amber-100 text-amber-600' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            <Mail className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium truncate max-w-[300px]">{log.subject}</span>
                              <Badge 
                                variant={log.status === 'sent' ? 'default' : log.status === 'failed' ? 'destructive' : 'secondary'}
                                className="text-xs"
                              >
                                {log.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              <span className="font-medium">To:</span> {log.to_email}
                            </p>
                            {log.sent_by_name && (
                              <p className="text-sm text-muted-foreground">
                                <span className="font-medium">Triggered by:</span> {log.sent_by_name} ({log.sent_by_email})
                              </p>
                            )}
                            {log.error && (
                              <p className="text-sm text-red-600 mt-1">
                                <span className="font-medium">Error:</span> {log.error}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">
                            {new Date(log.timestamp).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(log.timestamp).toLocaleTimeString('en-US', { 
                              hour: 'numeric', 
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* System Notifications Tab */}
          <TabsContent value="whats-new">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      What's New
                    </CardTitle>
                    <CardDescription>
                      Announce new features or changes. Each post shows as a slide-in card for every user; once a user clicks "Got it" it never re-appears for them.
                    </CardDescription>
                  </div>
                  <Button onClick={openWhatsNewNew} data-testid="whats-new-create-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Post
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {whatsNewPosts.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">
                    No posts yet. Click "New Post" to create your first announcement.
                  </div>
                ) : (
                  whatsNewPosts.map((p) => (
                    <div key={p.id} className="border rounded-lg p-4 space-y-2" data-testid={`whats-new-row-${p.id}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          <h4 className="font-semibold truncate">{p.title}</h4>
                          <Badge variant={p.active ? "default" : "secondary"}>
                            {p.active ? "Active" : "Inactive"}
                          </Badge>
                          {(p.audience && p.audience.length > 0) ? (
                            p.audience.map((r) => (
                              <Badge key={r} variant="outline" className="capitalize" data-testid={`whats-new-audience-${p.id}-${r}`}>
                                {r}
                              </Badge>
                            ))
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">Everyone</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => toggleWhatsNewActive(p)} data-testid={`whats-new-toggle-${p.id}`}>
                            {p.active ? "Deactivate" : "Activate"}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openWhatsNewEdit(p)} data-testid={`whats-new-edit-${p.id}`}>
                            Edit
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteWhatsNew(p.id)} data-testid={`whats-new-delete-${p.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div
                        className="prose prose-sm max-w-none text-foreground/80 [&_h2]:text-base [&_h2]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-primary [&_a]:underline"
                        dangerouslySetInnerHTML={{ __html: p.body_html }}
                      />
                      <div className="text-xs text-muted-foreground">
                        Created {new Date(p.created_at).toLocaleString()} by {p.created_by_name || "Unknown"}
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Editor dialog */}
            <Dialog open={!!whatsNewEditing} onOpenChange={(o) => !o && closeWhatsNewEditor()}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{whatsNewEditing?.id ? "Edit Post" : "New What's New Post"}</DialogTitle>
                </DialogHeader>
                {whatsNewEditing && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="whatsnew-title">Title</Label>
                      <Input
                        id="whatsnew-title"
                        data-testid="whats-new-title-input"
                        value={whatsNewEditing.title}
                        onChange={(e) => setWhatsNewEditing({ ...whatsNewEditing, title: e.target.value })}
                        placeholder="e.g. New: edit appointments inline"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Body</Label>
                      <RichTextEditor
                        value={whatsNewEditing.body_html}
                        onChange={(html) => setWhatsNewEditing({ ...whatsNewEditing, body_html: html })}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="whatsnew-active"
                        checked={whatsNewEditing.active}
                        onCheckedChange={(c) => setWhatsNewEditing({ ...whatsNewEditing, active: !!c })}
                      />
                      <Label htmlFor="whatsnew-active" className="text-sm">
                        Active (visible to users who haven't dismissed it)
                      </Label>
                    </div>
                    <div className="space-y-2">
                      <Label>Audience</Label>
                      <p className="text-xs text-muted-foreground">
                        Pick the roles who should see this post. Leave all unchecked to show it to <b>everyone</b>.
                      </p>
                      <div className="flex flex-wrap gap-3 pt-1">
                        {["therapist", "facilitator", "admin"].map((role) => {
                          const checked = (whatsNewEditing.audience || []).includes(role);
                          return (
                            <label
                              key={role}
                              className="flex items-center gap-2 px-3 py-1.5 border rounded-md cursor-pointer hover:bg-secondary/50 transition-colors"
                              data-testid={`whats-new-audience-toggle-${role}`}
                            >
                              <Checkbox checked={checked} onCheckedChange={() => toggleAudienceRole(role)} />
                              <span className="text-sm capitalize">{role}s</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    {whatsNewEditing.id && (
                      <div className="flex items-center gap-2 p-3 border rounded-md bg-amber-50">
                        <Checkbox
                          id="whatsnew-reset"
                          checked={!!whatsNewEditing.reset_dismissals}
                          onCheckedChange={(c) => setWhatsNewEditing({ ...whatsNewEditing, reset_dismissals: !!c })}
                        />
                        <Label htmlFor="whatsnew-reset" className="text-sm">
                          Re-show to everyone (clear all previous dismissals)
                        </Label>
                      </div>
                    )}
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={closeWhatsNewEditor} disabled={whatsNewSaving}>Cancel</Button>
                  <Button onClick={saveWhatsNew} disabled={whatsNewSaving} data-testid="whats-new-save-btn">
                    {whatsNewSaving ? "Saving…" : (whatsNewEditing?.id ? "Save Changes" : "Publish")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Session Audit Trails Tab */}
          <TabsContent value="session-audits">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Session Audit Trails
                    </CardTitle>
                    <CardDescription>
                      Comprehensive end-to-end tracking of each session - from booking to completion.
                      This data is read-only and cannot be modified.
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const res = await axios.post(`${API}/admin/session-audit-trails/rebuild-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success(res.data.message || "Audit trails rebuilt");
                          handleRefresh();
                        } catch (err) {
                          toast.error("Failed to rebuild audit trails");
                        }
                      }}
                      variant="outline"
                      size="sm"
                      data-testid="rebuild-audit-trails-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" /> Rebuild Trails
                    </Button>
                    <Button 
                      onClick={handleExportAuditTrails}
                      disabled={exportingAudit || sessionAuditTrails.length === 0}
                      variant="outline"
                    >
                      {exportingAudit ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Exporting...</>
                      ) : (
                        <><Download className="w-4 h-4 mr-2" /> Export CSV</>
                      )}
                    </Button>
                  </div>
                </div>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by patient name, therapist, or facility..."
                    value={auditSearch}
                    onChange={(e) => setAuditSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {sessionAuditTrails.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-semibold text-lg mb-2">No Session Audits Yet</h3>
                    <p className="text-muted-foreground mb-4">
                      Session audit trails will appear here after sessions are completed.
                      Use "Rebuild Trails" to generate audit records for existing sessions.
                    </p>
                    <Button 
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const res = await axios.post(`${API}/admin/session-audit-trails/rebuild-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success(res.data.message || "Audit trails rebuilt");
                          handleRefresh();
                        } catch (err) {
                          toast.error("Failed to rebuild audit trails");
                        }
                      }}
                      variant="outline"
                      size="sm"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" /> Rebuild Trails Now
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sessionAuditTrails
                      .filter(trail => {
                        if (!auditSearch) return true;
                        const search = auditSearch.toLowerCase();
                        return (
                          trail.patient_name?.toLowerCase().includes(search) ||
                          trail.therapist_name?.toLowerCase().includes(search) ||
                          trail.facility?.toLowerCase().includes(search) ||
                          trail.booked_by_name?.toLowerCase().includes(search)
                        );
                      })
                      .map((trail) => (
                        <div 
                          key={trail.id} 
                          className="border rounded-lg p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => {
                            setSelectedAuditTrail(trail);
                            setAuditDetailOpen(true);
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold">{trail.patient_name || "Unknown Patient"}</h4>
                                <Badge variant={trail.session_outcome === "completed" ? "default" : "secondary"}>
                                  {trail.session_outcome || "Unknown"}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {trail.discipline} • {trail.visit_type} • {trail.facility || "No facility"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Therapist: <span className="font-medium">{trail.therapist_name || "Unassigned"}</span>
                                {trail.booked_by_name && <> • Booked by: <span className="font-medium">{trail.booked_by_name}</span></>}
                              </p>
                            </div>
                            <div className="text-right text-sm">
                              <p className="text-muted-foreground">
                                {trail.scheduled_time ? new Date(trail.scheduled_time).toLocaleDateString('en-US', {
                                  month: 'short', day: 'numeric', year: 'numeric'
                                }) : "No date"}
                              </p>
                              {trail.total_session_duration && (
                                <p className="font-medium text-emerald-600">
                                  Duration: {formatDuration(trail.total_session_duration)}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Timeline summary */}
                          <div className="mt-3 pt-3 border-t flex flex-wrap gap-4 text-xs text-muted-foreground">
                            {trail.booked_at && (
                              <span>Booked: {new Date(trail.booked_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                            )}
                            {trail.patient_checked_in_at && (
                              <span>Checked In: {new Date(trail.patient_checked_in_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                            )}
                            {trail.session_created_at && (
                              <span>Session Started: {new Date(trail.session_created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                            )}
                            {trail.session_ended_at && (
                              <span>Ended: {new Date(trail.session_ended_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Impersonation Tab */}
          <TabsContent value="impersonation" data-testid="impersonation-tab-content">
            <ImpersonationPanel />
          </TabsContent>
        </Tabs>

        {/* Audit Trail Detail Dialog */}
        <Dialog open={auditDetailOpen} onOpenChange={setAuditDetailOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Session Audit Trail</DialogTitle>
              <DialogDescription>
                Complete end-to-end tracking for this session. This data cannot be modified.
              </DialogDescription>
            </DialogHeader>
            {selectedAuditTrail && (
              <div className="space-y-6">
                {/* Patient & Appointment Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm text-muted-foreground">Patient Information</h4>
                    <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                      <p><span className="font-medium">Name:</span> {selectedAuditTrail.patient_name}</p>
                      <p><span className="font-medium">Facility:</span> {selectedAuditTrail.facility || "—"}</p>
                      <p><span className="font-medium">State:</span> {selectedAuditTrail.state || "—"}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm text-muted-foreground">Appointment Details</h4>
                    <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                      <p><span className="font-medium">Discipline:</span> {selectedAuditTrail.discipline || "—"}</p>
                      <p><span className="font-medium">Visit Type:</span> {selectedAuditTrail.visit_type || "—"}</p>
                      <p><span className="font-medium">Scheduled:</span> {selectedAuditTrail.scheduled_time ? new Date(selectedAuditTrail.scheduled_time).toLocaleString() : "—"}</p>
                      <p><span className="font-medium">Duration:</span> {selectedAuditTrail.scheduled_duration_minutes || "—"} minutes</p>
                    </div>
                  </div>
                </div>
                
                {/* Therapist & Booking Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm text-muted-foreground">Therapist</h4>
                    <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                      <p><span className="font-medium">Name:</span> {selectedAuditTrail.therapist_name || "—"}</p>
                      <p><span className="font-medium">Block ID:</span> {selectedAuditTrail.availability_block_id?.substring(0, 8) || "—"}...</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm text-muted-foreground">Booking</h4>
                    <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                      <p><span className="font-medium">Booked By:</span> {selectedAuditTrail.booked_by_name || "—"}</p>
                      <p><span className="font-medium">Booked At:</span> {selectedAuditTrail.booked_at ? new Date(selectedAuditTrail.booked_at).toLocaleString() : "—"}</p>
                    </div>
                  </div>
                </div>
                
                {/* Timeline */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm text-muted-foreground">Session Timeline</h4>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <div className="flex-1">
                          <p className="font-medium">Appointment Booked</p>
                          <p className="text-sm text-muted-foreground">{selectedAuditTrail.booked_at ? new Date(selectedAuditTrail.booked_at).toLocaleString() : "—"}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${selectedAuditTrail.patient_checked_in_at ? 'bg-yellow-500' : 'bg-gray-300'}`}></div>
                        <div className="flex-1">
                          <p className="font-medium">Patient Checked In</p>
                          <p className="text-sm text-muted-foreground">
                            {selectedAuditTrail.patient_checked_in_at ? new Date(selectedAuditTrail.patient_checked_in_at).toLocaleString() : "—"}
                            {selectedAuditTrail.patient_checked_in_by && <span className="ml-2">(by {selectedAuditTrail.patient_checked_in_by})</span>}
                          </p>
                        </div>
                        {selectedAuditTrail.time_booking_to_checkin && (
                          <Badge variant="outline">{formatDuration(selectedAuditTrail.time_booking_to_checkin)}</Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${selectedAuditTrail.patient_marked_ready_at ? 'bg-purple-500' : 'bg-gray-300'}`}></div>
                        <div className="flex-1">
                          <p className="font-medium">Patient Marked Ready</p>
                          <p className="text-sm text-muted-foreground">
                            {selectedAuditTrail.patient_marked_ready_at ? new Date(selectedAuditTrail.patient_marked_ready_at).toLocaleString() : "—"}
                            {selectedAuditTrail.patient_marked_ready_by && <span className="ml-2">(by {selectedAuditTrail.patient_marked_ready_by})</span>}
                          </p>
                        </div>
                        {selectedAuditTrail.time_checkin_to_ready && (
                          <Badge variant="outline">{formatDuration(selectedAuditTrail.time_checkin_to_ready)}</Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${selectedAuditTrail.session_created_at ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <div className="flex-1">
                          <p className="font-medium">Session Created (Therapist Started)</p>
                          <p className="text-sm text-muted-foreground">
                            {selectedAuditTrail.session_created_at ? new Date(selectedAuditTrail.session_created_at).toLocaleString() : "—"}
                            {selectedAuditTrail.session_created_by_name && <span className="ml-2">(by {selectedAuditTrail.session_created_by_name})</span>}
                          </p>
                        </div>
                        {selectedAuditTrail.time_ready_to_session_start && (
                          <Badge variant="outline">{formatDuration(selectedAuditTrail.time_ready_to_session_start)}</Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${selectedAuditTrail.facilitator_joined_at ? 'bg-teal-500' : 'bg-gray-300'}`}></div>
                        <div className="flex-1">
                          <p className="font-medium">Facilitator Joined Video</p>
                          <p className="text-sm text-muted-foreground">
                            {selectedAuditTrail.facilitator_joined_at ? new Date(selectedAuditTrail.facilitator_joined_at).toLocaleString() : "—"}
                            {selectedAuditTrail.facilitator_name && <span className="ml-2">({selectedAuditTrail.facilitator_name})</span>}
                          </p>
                        </div>
                        {selectedAuditTrail.time_session_start_to_facilitator_join && (
                          <Badge variant="outline">{formatDuration(selectedAuditTrail.time_session_start_to_facilitator_join)}</Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${selectedAuditTrail.session_ended_at ? 'bg-gray-500' : 'bg-gray-300'}`}></div>
                        <div className="flex-1">
                          <p className="font-medium">Session Ended</p>
                          <p className="text-sm text-muted-foreground">
                            {selectedAuditTrail.session_ended_at ? new Date(selectedAuditTrail.session_ended_at).toLocaleString() : "—"}
                            {selectedAuditTrail.session_ended_by_name && <span className="ml-2">(by {selectedAuditTrail.session_ended_by_name})</span>}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Time Metrics Summary */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm text-muted-foreground">Time Metrics</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-600">{formatDuration(selectedAuditTrail.total_session_duration)}</p>
                      <p className="text-xs text-muted-foreground">Total Session Duration</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-600">{formatDuration(selectedAuditTrail.total_video_call_duration)}</p>
                      <p className="text-xs text-muted-foreground">Video Call Duration</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-amber-600">{formatDuration(selectedAuditTrail.time_ready_to_session_start)}</p>
                      <p className="text-xs text-muted-foreground">Wait Time (Ready → Start)</p>
                    </div>
                  </div>
                </div>
                
                {/* Session Outcome */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div>
                    <p className="text-sm text-muted-foreground">Session Outcome</p>
                    <Badge variant={selectedAuditTrail.session_outcome === "completed" ? "default" : "secondary"} className="mt-1">
                      {selectedAuditTrail.session_outcome || "Unknown"}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Session ID</p>
                    <code className="text-xs bg-muted px-2 py-1 rounded">{selectedAuditTrail.session_id}</code>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
